import ProviderInvoiceFormTotalsConfig from "./formTotals";
import ProviderInvoiceFormConfig from "./form";
import GenericForm from "../GenericForm";
import { ProviderInvoiceFormData } from "@/formTypes/ProviderInvoiceDataType";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getDefaultCurrency } from "@utils/Currency";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { CurrencyIdPubSub } from "@utils/pubsubs/PubSub";
import { flushDocumentProductEdits } from "@utils/eDocumentFlush";
import LoadXmlDataForm from "@components/dynamic/LoadXmlDataForm";
import { loadXmlDocument, XMLDocumentData } from "@utils/EDocument";
import LoadingScreen from "@components/content/LoadingScreen";
import { ProviderInvoiceCategoryApi } from "@/api/ProviderInvoiceCategoryApi";
import { omitProperties, toNumberFormat } from "@utils/Utils";
import { roundCant } from "@utils/Cant";
import { PROVIDER_INVOICE_CASH_TYPES } from "@utils/Constants";
import { showAlert } from "@utils/Alert";
import { NotFoundError } from "@/api/ApiClient";
import { Col, Row } from "reactstrap";
import QuickAddProductForm from "../QuickAddProductForm";
import EdocumentProductList from "@components/dynamic/EdocumentProductList";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearDocument, loadProducts } from "@/store/slices/eDocumentSlides";
import { ProductApi } from "@/api/ProductsApi";
import { ProviderApi } from "@/api/ProviderApi";
import { loadReferenceFormLabel } from "@utils/Forms";

type ProviderInvoiceInventoryFormProps = {
  formId: string,
  saveRedirectUrl: string
}

const ProviderInvoiceInventoryForm = (props: ProviderInvoiceInventoryFormProps) => {
  const [defaultData, setDefaultData] = useState<ProviderInvoiceDataType>({});
  const formRef = useRef<DynamicFormRef>(null);
  const formTotalRef = useRef<DynamicFormRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const documentProducts = useAppSelector(state => state.eDocument.documentProducts);
  const dispatch = useAppDispatch();
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const [formConfig, setFormConfig] = useState<any>();

  useEffect(() => {
    loadFormConfig()
  }, [suscriptionData])

  useLayoutEffect(() => {
    resetFormData();
  }, []);

  const loadFormConfig = () => {
    const formConfig = loadReferenceFormLabel(ProviderInvoiceFormConfig, suscriptionData);
    if (suscriptionData?.id) {
      setFormConfig(formConfig)
    }
  }

  const resetFormData = async () => {
    formRef.current?.resetFormValue()
    formTotalRef.current?.resetFormValue()
    setTimeout(async () => {
      const defaultCurrency = await getDefaultCurrency()
      if (defaultCurrency.id) {
        setDefaultData({ currencyId: defaultCurrency.id })
        CurrencyIdPubSub.publish({ currencyId: defaultCurrency.id, forceUpload: false })
      }
    });
  }

  const onInputChange = async (name: keyof ProviderInvoiceDataType, value: any) => {
    // if (name == "clientId") {
    //   const clientData = await ClientApi.get(value);
    //   formRef.current?.setFormValue("nombre", `${clientData.nombre}`, "String");
    // }
  }

  const onXMLFileLoad = async (fileData: unknown) => {
    setIsLoading(true)
    try {
      const { documentData } = await loadXmlDocument(fileData, ["FacturaElectronica"])
      const defaultCategoria = await ProviderInvoiceCategoryApi.find({ is_default: { eq: true } });
      const providerInvoiceData = omitProperties(documentData || {}, ["Products", "identificacion"])

      const alreadyAddedDocument = await documentExist(`${documentData?.document_key}`)
      if (alreadyAddedDocument?.id) {
        showAlert("warning", "Esta factura ya se encuentra registrada en el sistema")
      }

      const defaultData: Partial<ProviderInvoiceFormData> = {
        ...providerInvoiceData,
        id: alreadyAddedDocument?.id,
        providerInvoiceCategoryId: defaultCategoria?.id || ""
      }
      formRef.current?.resetFormValue(defaultData);
      const { subtotal, descuento, impuestos, total } = defaultData
      formTotalRef.current?.resetFormValue({ subtotal, descuento, impuestos, total });
      if (documentData) {
        loadInvoiceLines(documentData)
      }
    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", err.message)
      }
    }
    finally {
      setIsLoading(false)
    }
  }

  const loadInvoiceLines = (documentData: XMLDocumentData) => {
    const products = documentData.Products.map(p => {
      const product: Partial<EDocumentProductDataType> = {
        cant: roundCant(p.cant),
        codigo: p.codigo,
        description: p.descripcion,
        price: p.costo,
        subtotal: Number(toNumberFormat(p.costo * p.cant)),
      }
      return product
    })

    dispatch(loadProducts(products as EDocumentProductDataType[]))
  }

  const documentExist = async (documentKey: string): Promise<ProviderInvoiceDataType> => {
    try {
      return await ProviderInvoiceApi.getInvoiceByDocumentKey(documentKey);
    } catch (err) {
      if (err instanceof NotFoundError) {
        return {} as ProviderInvoiceDataType
      } else {
        throw err
      }
    }
  }

  const saveInvoiceAndLoadInventory = async (): Promise<ProviderInvoiceDataType> => {
    const eDocumentState = flushDocumentProductEdits();
    const documentProductsState = eDocumentState.documentProducts;
    const primaryData = await formRef.current?.getFormData();
    const totalData = await formTotalRef.current?.getFormData();

    let invoiceData: ProviderInvoiceDataType = {
      ...primaryData,
      ...totalData,
      status: "pendiente"
    }
    if (`${invoiceData.tipo}` == `${PROVIDER_INVOICE_CASH_TYPES}`) {
      invoiceData.status = "pagada"
    }

    const pendingAsignmentProduct = documentProductsState?.some(p => !p.productId);
    if (pendingAsignmentProduct) {
      throw new Error("Por favor asigne todos los producto de la factura, o elimine los productos que no son necesarios de agregar al inventario")
    }

    const provedor = await ProviderApi.get(`${invoiceData.providerId}`)
    const providerInvoice = await ProviderInvoiceApi.save(invoiceData)

    await ProductApi.increaseStock(documentProductsState as EDocumentProductDataType[], `Aumento de inventario por ingreso de factura #${invoiceData.numero} del provedor ${provedor.nombre}`)
    const costLogPromises = documentProductsState?.map(p => {
      return ProductApi.updateProductCostIfDifferent(`${invoiceData.providerId}`, `${p?.productId}`, Number(p?.price))
    })
    if (costLogPromises && costLogPromises?.length > 0) {
      await Promise.all(costLogPromises);
    }

    dispatch(clearDocument())
    resetFormData()

    return providerInvoice
  }

  return (
    <>
      {formConfig?.sections && (<>
        <LoadXmlDataForm
          paramLoadable={true}
          onFileLoaded={onXMLFileLoad}
        />
        <GenericForm<ProviderInvoiceFormData, ProviderInvoiceDataType>
          ref={formRef}
          formId={props.formId}
          defaultData={defaultData}
          onInputChange={onInputChange}
          formConfig={formConfig}
          getById={(...arg) => ProviderInvoiceApi.get(...arg)}
          createFn={() => saveInvoiceAndLoadInventory()}
          updateFn={() => saveInvoiceAndLoadInventory()}
          successMessage="La Factura del Provedor ha sido almacenada correctamente y el inventario a sido cargado existosamente"
          errorMessage="No fue posible almacenar la información de la Factura del provedor"
        >
          <hr />
          <QuickAddProductForm showCost={true} />
          <EdocumentProductList providerDocument={true} />
        </GenericForm>
        <Row>
          <Col>
          </Col>
          <Col xs="5">
            <GenericForm<ProviderInvoiceFormData, ProviderInvoiceDataType>
              ref={formTotalRef}
              formId={`${props.formId}-totals`}
              defaultData={defaultData}
              saveRedirectUrl={props.saveRedirectUrl}
              onInputChange={onInputChange}
              formConfig={ProviderInvoiceFormTotalsConfig}
              createFn={() => saveInvoiceAndLoadInventory()}
              updateFn={() => saveInvoiceAndLoadInventory()}
            />
          </Col>
        </Row>
        <LoadingScreen isLoading={isLoading} />
      </>)}
    </>
  )
}

export default ProviderInvoiceInventoryForm;